import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Stack, Divider } from "@mui/material";
import { motion } from "framer-motion";

const PrivacyPolicyPage = () => {
  const { t } = useTranslation();

  const lastUpdated = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const controllerItems = t("legal.privacy.controller.items", { returnObjects: true });
  const processingSections = t("legal.privacy.processing.sections", { returnObjects: true });
  const purposesItems = t("legal.privacy.purposes.items", { returnObjects: true });
  const recipientsItems = t("legal.privacy.recipients.items", { returnObjects: true });
  const retentionItems = t("legal.privacy.retention.items", { returnObjects: true });
  const rightsItems = t("legal.privacy.rights.items", { returnObjects: true });

  return (
    <Box component="main" sx={{ bgcolor: "#0B0F14", minHeight: "100vh", pb: 20 }}>
      <Box sx={{ py: 15, bgcolor: "#111827", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <Container maxWidth="md">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Typography
              variant="overline"
              align="center"
              sx={{ color: "#0FA3A3", fontWeight: 700, letterSpacing: 3, display: "block", mb: 2 }}
            >
              {t("legal.privacy.lastUpdatedLabel").toUpperCase()} — {lastUpdated}
            </Typography>
            <Typography
              variant="h1"
              align="center"
              sx={{ color: "white", mb: 3, fontFamily: "Philosopher", fontSize: { xs: "2.25rem", md: "3.25rem" } }}
            >
              {t("legal.privacy.title")}
            </Typography>
            <Typography
              variant="h6"
              align="center"
              sx={{ color: "#9CA3AF", fontWeight: 300, lineHeight: 1.6, maxWidth: "760px", mx: "auto" }}
            >
              {t("legal.privacy.intro")}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: 10 }}>
        <Stack spacing={6}>
          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.privacy.controller.title")}
            </Typography>
            <Stack spacing={1}>
              {controllerItems.map((item, idx) => (
                <Typography key={idx} variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
                  {item}
                </Typography>
              ))}
            </Stack>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />

          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.privacy.processing.title")}
            </Typography>
            <Stack spacing={3}>
              {processingSections.map((section, idx) => (
                <Box key={idx}>
                  <Typography variant="h6" sx={{ color: "#D4AF37", fontWeight: 800, mb: 1 }}>
                    {section.title}
                  </Typography>
                  <Stack spacing={0.75}>
                    {(section.items || []).map((item, jdx) => (
                      <Typography key={jdx} variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
                        {item}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />

          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.privacy.purposes.title")}
            </Typography>
            <Stack spacing={1}>
              {purposesItems.map((item, idx) => (
                <Typography key={idx} variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
                  {item}
                </Typography>
              ))}
            </Stack>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />

          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.privacy.recipients.title")}
            </Typography>
            <Typography variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8, mb: 2 }}>
              {t("legal.privacy.recipients.intro")}
            </Typography>
            <Stack spacing={1}>
              {recipientsItems.map((item, idx) => (
                <Typography key={idx} variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
                  {item}
                </Typography>
              ))}
            </Stack>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />

          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.privacy.retention.title")}
            </Typography>
            <Stack spacing={1}>
              {retentionItems.map((item, idx) => (
                <Typography key={idx} variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
                  {item}
                </Typography>
              ))}
            </Stack>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />

          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.privacy.transfers.title")}
            </Typography>
            <Typography variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
              {t("legal.privacy.transfers.text")}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />

          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.privacy.security.title")}
            </Typography>
            <Typography variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
              {t("legal.privacy.security.text")}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />

          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.privacy.rights.title")}
            </Typography>
            <Typography variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8, mb: 2 }}>
              {t("legal.privacy.rights.intro")}
            </Typography>
            <Stack spacing={1}>
              {rightsItems.map((item, idx) => (
                <Typography key={idx} variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
                  {item}
                </Typography>
              ))}
            </Stack>
            <Typography variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8, mt: 2 }}>
              {t("legal.privacy.rights.contact")}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />

          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.privacy.aiAct.title")}
            </Typography>
            <Typography variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
              {t("legal.privacy.aiAct.text")}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />

          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.privacy.changes.title")}
            </Typography>
            <Typography variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
              {t("legal.privacy.changes.text")}
            </Typography>
          </Box>

          <Box sx={{ p: 4, bgcolor: "rgba(212, 175, 55, 0.05)", borderRadius: "20px", border: "1px solid rgba(212, 175, 55, 0.15)" }}>
            <Typography variant="body2" sx={{ color: "#D4AF37", lineHeight: 1.8, fontWeight: 700 }}>
              {t("legal.privacy.disclaimer")}
            </Typography>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;
